import React, { useEffect, useMemo } from "react";

import { createRoot } from "react-dom/client";
import { Route, Routes, Navigate, useLocation } from "react-router-dom";

// eslint-disable-next-line import/no-unresolved
import { WherebyProvider } from "@whereby.com/browser-sdk/react";

import Grid from "@mui/material/Grid";
import Fade from "@mui/material/Fade";
import CircularProgress from "@mui/material/CircularProgress";

import { AnimatePresence } from "framer-motion";

import { styled } from "@mui/material/styles";

import "./lib/dayjs";
import "./lib/i18n";

import Board from "./bit/board";
import { ThemeProvider } from "./bit/theme";
import { LanguageProvider } from "./bit/language";
// import { TrackingProvider } from "./bit/tracking";
import { SchedulesProvider, ScreensaverRouter } from "./bit/schedules";

import Footer from "./components/Footer";
import Header from "./components/Header";
import OfflineAlert from "./components/Offline/OfflineAlert";

import * as Pages from "./pages";

import useQueryBoard from "./lib/hooks/useQueryBoard";
import OfflineProvider, { useOfflineContext } from "./context/Offline";
import LogoImage from "./components/LogoImage";
import useCursorVisibility from "./lib/hooks/useCursorState";

const isDev = process.env.NODE_ENV === "development";

const Contact = React.lazy(() => import("./pages/Contact"));
const QRCodeScanner = React.lazy(() => import("./pages/QRCodeScanner"));
const ScannerProvider = React.lazy(() => import("./context/Scanner"));

const Container = styled("div")(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
}));

const LazyScannerProvider = React.memo<{ children: React.ReactNode }>(
  (props) => {
    const { children } = props;

    const { data } = useQueryBoard();

    const socketURL = useMemo(
      () => data?.digitalReception?.metadata?.socket?.url,
      [data],
    );

    if (!socketURL) {
      return <>{children}</>;
    }

    return (
      <React.Suspense>
        <ScannerProvider url={socketURL}>{children}</ScannerProvider>
      </React.Suspense>
    );
  },
);

const Activation = () => (
  <Container className="w-100 d-flex flex-column position-relative overflow-hidden">
    <div
      className="flex-grow-1 d-flex flex-column justify-content-center align-items-center"
      style={{ marginTop: "-5%" }}
    >
      <LogoImage />
      {/*

      Activation Router allows only one route
      which leads to the activation page

      isDev condition resolves the navigation in development mode
      because the server handles the navigation in production

      */}
      <Routes>
        <Route
          path={isDev ? "/activation" : "/"}
          element={<Pages.Activation />}
        />
        <Route
          path="*"
          element={<Navigate to={isDev ? "/activation" : "/"} replace />}
        />
      </Routes>
    </div>
  </Container>
);

const AppRouter = () => {
  const location = useLocation();

  const { data } = useQueryBoard();

  const isAIConversationEnabled = useMemo(
    () => data?.digitalReception?.metadata?.aiConversation || false,
    [],
  );

  const isPremium = useMemo(() => {
    const plan = data?.digitalReception?.client?.plan;

    return plan && plan !== "free";
  }, [data]);

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/access/option/:action" element={<Pages.CheckOptions />} />

        <Route path="/access/checkin/manual" element={<Pages.ManualCheckin />}>
          <Route
            path="contact/:id/:callType"
            element={<Pages.Calling type="employee" />}
          />
        </Route>

        <Route
          path="/access/checkout/manual"
          element={<Pages.ManualCheckout />}
        />

        <Route path="/access/:action" element={<Pages.Check />}>
          <Route path=":code" element={<Pages.Scanning />} />
          <Route
            path="contact/:id/:callType"
            element={<Pages.Calling type="employee" />}
          />
        </Route>

        <Route path="/delivery/:action" element={<Pages.Delivery />}>
          <Route
            path="contact/:id/:callType"
            element={<Pages.Calling type="employee" />}
          />
        </Route>

        <Route
          path="/custom-delivery/:action"
          element={<Pages.CustomDelivery />}
        >
          <Route
            path="contact/:id/:callType"
            element={<Pages.Calling type="employee" />}
          />
        </Route>

        {isPremium && (
          <>
            <Route path="/contact/:id/*" element={<Pages.Contact />} />
            <Route path="/video-call" element={<Pages.VideoCall />} />
            <Route path="/scanner/:action" element={<Pages.QRCodeScanner />} />
          </>
        )}

        <Route path="/overview" element={<Pages.Overview />} />

        <Route path="/wifi" element={<Pages.Wifi />} />

        {isAIConversationEnabled && (
          <Route path="/conversation" element={<Pages.Conversation />} />
        )}

        <Route path="/" element={<Pages.Home />}>
          <Route path="scan/:code" element={<Pages.Scanning />} />
          {isPremium && (
            <Route
              path="contact/reception/call"
              element={<Pages.Calling type="reception" />}
            />
          )}
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AnimatePresence>
  );
};

const Providers = React.memo<{ children: React.ReactNode }>((props) => {
  const { children } = props;

  const { data, isLoading, isError, dataUpdatedAt } = useQueryBoard();
  const { online } = useOfflineContext();

  // hide the cursor in production
  useCursorVisibility();

  const isActivation = window.location.pathname.includes("/activation");
  const isLoadingOnline = online && isLoading && !isError && !isActivation;

  return (
    <>
      <ThemeProvider
        theme={data?.digitalReception?.theme}
        dataUpdatedAt={dataUpdatedAt}
      >
        <LanguageProvider
          language={data?.digitalReception?.language}
          languages={data?.digitalReception?.languages}
        >
          {isActivation ? (
            <Activation />
          ) : (
            <>
              {/* <TrackingProvider> */}
              <LazyScannerProvider>
                <WherebyProvider>{children}</WherebyProvider>
                <ScreensaverRouter />
              </LazyScannerProvider>
              {/* </TrackingProvider> */}
            </>
          )}
        </LanguageProvider>
      </ThemeProvider>

      <OfflineAlert />

      <Fade in={isLoadingOnline} appear={false} unmountOnExit>
        <Grid
          container
          alignItems="center"
          justifyContent="center"
          className="position-absolute top-0 bottom-0 left-0 right-0 bg-white"
        >
          <Grid item>
            <CircularProgress size="8rem" />
          </Grid>
        </Grid>
      </Fade>
    </>
  );
});

const App = () => (
  <SchedulesProvider>
    <OfflineProvider>
      <Board>
        <Providers>
          <Container className="w-100 d-flex flex-column position-relative overflow-hidden">
            <Header />
            <main className="flex-grow-1">
              <AppRouter />
            </main>
            <Footer />
          </Container>
        </Providers>
      </Board>
    </OfflineProvider>
  </SchedulesProvider >
);

document.addEventListener("contextmenu", (event) => event.preventDefault());

const container = document.getElementById("root");
const root = createRoot(container!);

root.render(<App />);
